import type { LeaderboardSegment } from '@/lib/api/rewards-leaderboard/models';
import { DEFAULT_LEADERBOARD_VIEW, writeLeaderboardView } from '@/lib/utils/leaderboardQuery';

/** The hub's landing page, in the web zone. */
export const HUB_PATH = '/rewards';

/** My Collection on the hub: where "Go to Rewards Hub" sends a card picked from the global widget. */
export const COLLECTION_ANCHOR = 'collection';
export const HUB_COLLECTION_PATH = `${HUB_PATH}#${COLLECTION_ANCHOR}`;

/**
 * The leaderboard — where the header, its menus, the footer and search send
 * "Leaderboard" since it replaced the Stats page (19.10). Web zone.
 */
export const LEADERBOARD_PATH = '/rewards/leaderboard';

/**
 * Pages the global daily card stays off: the hub (it has the streak itself)
 * but not its leaderboard, and the pages where the player is busy with money,
 * settings or their inbox. Paths are locale-free, as `usePathname` gives them.
 */
const DAILY_CARD_HIDDEN_PREFIXES = [HUB_PATH, '/account', '/notifications'];

export function isDailyCardHiddenOn(pathname: string): boolean {
  if (pathname.startsWith(LEADERBOARD_PATH)) return false;
  return DAILY_CARD_HIDDEN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Where a won bonus lands: the Promo codes tab of the account page. */
export const PROMOCODES_HREF = '/account?tab=promocodes';

/**
 * The Promo codes tab opened on one code: `highlight` names it by promo code
 * id (what a claim answers as `pc`), `new` marks it as just arrived. Absolute,
 * with the locale, since it opens in a tab of its own.
 */
export function promocodeHref(locale: string, promoCodeId: string, isNew: boolean): string {
  const params = new URLSearchParams({ tab: 'promocodes', highlight: promoCodeId });
  if (isNew) params.set('new', '1');
  return `/${locale}/account?${params.toString()}`;
}

/**
 * The Promo codes tab on one card, for a link inside the web zone — same tab,
 * locale added by the router. The card is named by its promo code id or its
 * activation id; without either, just the tab.
 */
export function promocodesTabHref(cardId: string | null): string {
  if (!cardId) return PROMOCODES_HREF;
  return `/account?${new URLSearchParams({ tab: 'promocodes', highlight: cardId }).toString()}`;
}

/**
 * Log in / sign up in the auth zone. Cross-zone, so the locale is written out
 * and the link is a plain navigation, never a next-intl `<Link>`.
 */
export function authHref(locale: string, page: 'login' | 'signup'): string {
  return `/${locale}/auth/${page}`;
}

/** The leaderboard opened on `segment`'s tab — where the home column's card leads. */
export function leaderboardHref(segment: LeaderboardSegment): string {
  const params = new URLSearchParams();
  writeLeaderboardView(params, { ...DEFAULT_LEADERBOARD_VIEW, segment });
  const query = params.toString();
  return query ? `${LEADERBOARD_PATH}?${query}` : LEADERBOARD_PATH;
}

/**
 * A player's public trading profile, by user id — what the leaderboard and its
 * Live Trades link to (15.10). Same zone, so a next-intl `<Link>` adds the locale.
 */
export function playerProfileHref(userId: string): string {
  return `/stats/profile/${userId}`;
}
